import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalMarketing from "@faire/web-api--source/routes/brand-portal/marketing";
import brandPortalSamples from "@faire/web-api--source/routes/brand-portal/marketing/samples";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const SamplesIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you want to offer food and drink samples to retailers, follow these steps:"
          description={{
            text: "Introduction to offering samples",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to the <LinkFormatting>Marketing</LinkFormatting> page in your brand portal."
              values={{
                LinkFormatting: MarketingLinkFormatting,
              }}
              description={{
                text: "First step to offer samples",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <LinkFormatting>Samples</LinkFormatting>."
              values={{
                LinkFormatting: SamplesLinkFormatting,
              }}
              description={{
                text: "Second step to offer samples",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="You will be given three ways to start offering samples:"
              description={{
                text: "Third step to offer samples",
              }}
            />
          </Typography>
        </LiItem>
        <Row>
          <Spacer width="2x" />
          <LiItem step="a">
            <Typography>
              <StrictLocalMsg
                defaultMessage="Allow retailers on the Faire marketplace to request a sample before placing an opening order (inbound sampling)"
                description={{
                  text: "First substep of third step to offer samples",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="b">
            <Typography>
              <StrictLocalMsg
                defaultMessage="Share a link Faire will provide you with to promote samples to customers, even if they’re not on Faire (outbound sampling)"
                description={{
                  text: "Second substep of third step to offer samples",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="c">
            <Typography>
              <StrictLocalMsg
                defaultMessage="Create an email campaign to promote your samples to your customers"
                description={{
                  text: "Third substep of third step to offer samples",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Any order restrictions you’ve applied (such as restrictions on selling to online-only retailers or specific postal codes) will also apply to sample requests. If a retailer is unable to order samples despite not being affected by your shop restrictions, please direct them to contact our support team."
          description={{
            text: "Information about order restrictions",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="When you receive a sample request, a notification will appear next to the <BoldFormatting>Samples</BoldFormatting> section in your brand portal. Like other orders, you’ll have 30 days to fulfill or decline the request, and you can decline requests without impacting your store score via cancellations."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Information about handling sample requests",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Samples must be shipped using the <BoldFormatting>Ship on your own</BoldFormatting> option. Brands are responsible for shipping costs and must provide the carrier and tracking number to mark the request as fulfilled. Please don’t ship sample orders before fulfilling them in the Faire portal."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Information about shipping samples",
          }}
        />
      </Typography>
    </Column>
  );
};

const MarketingLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalMarketing()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const SamplesLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalSamples()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
